import { useCallback } from 'react';

import { copyToClipboard as copy } from '../copyToClipboard/index.ts';
import useBoolean from '../useBoolean/index.ts';
import useTimeout from '../useTimeout/index.ts';

function isString(value: unknown): value is string {
  return typeof value === 'string';
}

export interface ClipboardOptions {
  /**
   * The text to copy to the clipboard
   */
  text: string;
  /**
   * Fired when copied
   * @default noop
   */
  onCopy?: (text: string) => void;
  /**
   * Whether what is being copied is plain text or html
   * @default true
   */
  isPlainText?: boolean;
  /**
   * The amount of time to clear the copied state after a copy
   * If `0` then will not change states after a copy
   * @default 0
   */
  timeout?: number | string;
}

export interface Clipboard {
  /**
   * Copies the text to the clipboard
   */
  copy: (text?: string) => void;
  /**
   * Whether the clipboard has been copied from
   */
  isCopied: boolean;
}

/**
 * Copies text to the clipboard.
 *
 * @param {ClipboardOptions} options
 * @returns {Clipboard}
 *
 * @example <caption>Example Usage</caption>
 * const { copy } = useClipboard({ text, onCopy });
 * (<Button onClick={copy} />)
 */
export default function useClipboard(
  { text = '', onCopy = () => null, isPlainText = true, timeout = 0 }: ClipboardOptions = {
    text: ''
  }
): Clipboard {
  const { value: isCopied, setTrue: hasCopied, setFalse: clearCopied } = useBoolean(false);

  const copyToClipboard = useCallback(
    (t: string | undefined) => {
      const textToCopy = isString(t) ? t : text;
      copy(textToCopy, { isPlainText });

      hasCopied();
      onCopy(textToCopy);
    },
    [text, isPlainText, hasCopied, onCopy]
  );

  useTimeout(
    () => {
      if (isCopied && timeout) {
        clearCopied();
      }
    },
    timeout,
    [isCopied]
  );

  return {
    copy: copyToClipboard,
    isCopied
  };
}
